// Honeypot + time trap. The render time comes from the server (components/spam_guard.ejs),
// so there is no secret and no client clock involved.
// No imports and no Node APIs: browser bundles load withoutSpamFields() from here.

/** Form field names written by components/spam_guard.ejs. */
export const SPAM_FIELDS = { honeypot: 'website', renderedAt: 'renderedAt' } as const;
type SpamFieldName = (typeof SPAM_FIELDS)[keyof typeof SPAM_FIELDS];

export const MIN_FILL_MS = 3_000;

export type SpamReason = 'honeypot' | 'too-fast' | 'invalid';

export type SpamFields = { honeypot?: unknown; renderedAt?: unknown };

type Form = Record<string, unknown>;

/** The reason a submission looks like spam, or null. `invalid`: render time missing, malformed or in the future. */
export function checkSpam(
  { honeypot, renderedAt }: SpamFields,
  now = Date.now(),
  minMs = MIN_FILL_MS,
): SpamReason | null {
  if (honeypot !== undefined && honeypot !== null && honeypot !== '') return 'honeypot';
  const at = typeof renderedAt === 'string' && /^[0-9]+$/.test(renderedAt) ? Number(renderedAt) : renderedAt;
  if (typeof at !== 'number' || !Number.isSafeInteger(at) || at > now) return 'invalid';
  return now - at < minMs ? 'too-fast' : null;
}

/** Removes the guard's fields from the form, so the app's Joi schema never sees them. */
export function takeSpamFields(form: Form): SpamFields {
  const fields = { honeypot: form[SPAM_FIELDS.honeypot], renderedAt: form[SPAM_FIELDS.renderedAt] };
  delete form[SPAM_FIELDS.honeypot];
  delete form[SPAM_FIELDS.renderedAt];
  return fields;
}

/** A copy of the form without the guard's fields, for checking with the app's schema in the browser. */
export function withoutSpamFields<T extends Form>(form: T): Omit<T, SpamFieldName> {
  const copy: Form = { ...form };
  delete copy[SPAM_FIELDS.honeypot];
  delete copy[SPAM_FIELDS.renderedAt];
  return copy as Omit<T, SpamFieldName>;
}

type GuardOptions<Req, Res> = {
  /** Answer exactly like a successful submission, and persist nothing. */
  onSpam: (req: Req, res: Res, reason: SpamReason) => void;
  /** Returns the submitted form. Default: `req.body`. */
  read?: (req: Req) => unknown;
  /** Stores the stripped form when `read` returned a copy (e.g. parsed from a JSON string). */
  write?: (req: Req, form: Form) => void;
  minMs?: number;
};

/** Express 4/5 middleware. Register it after the body parser and before validation. */
export function spamGuard<Req, Res>({
  onSpam,
  read = (req) => (req as { body?: unknown }).body,
  write,
  minMs = MIN_FILL_MS,
}: GuardOptions<Req, Res>) {
  return (req: Req, res: Res, next: (err?: unknown) => void) => {
    const form = read(req);
    if (typeof form !== 'object' || form === null || Array.isArray(form)) {
      return next(new Error('spamGuard: no form object; register the body parser first'));
    }
    const reason = checkSpam(takeSpamFields(form as Form), Date.now(), minMs);
    write?.(req, form as Form);
    if (!reason) return next();
    // Only the reason: field values may contain personal data.
    console.warn(`Spam blocked: ${reason}`);
    return onSpam(req, res, reason);
  };
}
